import { Injectable, inject } from '@angular/core';
import { ProductAdditionalFields } from '@app/modules/configuration/products/product-form/product-form.component';
import { SampleFields } from '@app/modules/service-data/components/sample/sample-dialog/field-sets/sample-fields.enum';
import { sampleFieldConfiguration } from '@app/modules/service-data/components/sample/sample-dialog/sample-field-configuration';
import {
  Gender,
  PregnancyType,
} from '@app/modules/service-data/types/service-data.types';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import {
  DataInputType,
  PriceContract,
  Product,
  ProductConfiguration,
} from '../core.types';
import { LoadProductConfigurationById } from '../store/product-configuration/product-configuration.actions';
import { ProductConfigurationsState } from '../store/product-configuration/product-configuration.state';
import { ProductsListState } from '../store/products/products.state';
import { Logger } from './logger.service';
import { PantheonService } from './pantheon.service';
import { ProductsService } from './products.service';

@Injectable({
  providedIn: 'root',
})
export class ProductUtilsService {
  private store = inject(Store);
  private productsService = inject(ProductsService);
  private pantheonService = inject(PantheonService);

  private readonly log = new Logger(this.constructor.name);

  private readonly productsOrder = [
    'pro',
    'plus',
    'standard',
    'basic',
    'twins',
  ];

  getProductById$(productId: string): Observable<Product> {
    return this.store.select(ProductsListState.getProduct).pipe(
      map((filterByProduct) => filterByProduct(productId)),
      distinctUntilChanged()
    );
  }

  getProductConfigurationById$(
    productConfigurationId: string
  ): Observable<ProductConfiguration> {
    this.log.debug(
      'resolve product configuration by id',
      productConfigurationId
    );
    if (productConfigurationId) {
      return this.store
        .dispatch(new LoadProductConfigurationById(productConfigurationId))
        .pipe(
          switchMap(() =>
            this.store
              .select(ProductConfigurationsState.getProductConfigurationById)
              .pipe(
                map(
                  (
                    filterByProductConfigurationId: (
                      id: string
                    ) => ProductConfiguration
                  ) => filterByProductConfigurationId(productConfigurationId)
                ),
                filter((pc) => pc !== undefined)
              )
          )
        );
    }
    return of(null);
  }

  getPriceContactsFromPantheonByPartnerExternalId$(
    externalId: string
  ): Observable<PriceContract[]> {
    if (!externalId) {
      return of([]);
    }
    return this.pantheonService.getPriceContactsFromPantheonByPartnerExternalId(
      externalId
    );
  }

  getBundlesNumberOfFetuses(products: Product[]): PregnancyType[] {
    if (products.length === 0) return [];

    // Initialize a set with the types of the first product
    let commonTypes = new Set(Object.values(PregnancyType));

    // Iterate over the rest of the products
    for (const product of products) {
      if (!product.productSubTypes?.length) continue;
      const productTypesSet = new Set(product.productSubTypes);

      // Intersect the sets
      commonTypes = new Set(
        [...commonTypes].filter((type) => productTypesSet.has(type))
      );
    }

    // Convert the set back to an array
    return Array.from(commonTypes);
  }

  getBundlesGender(products: Product[]): Gender[] {
    if (products.length === 0) return [];

    // Initialize a set with the types of the first product
    let commonTypes = new Set(Object.values(Gender));

    // Iterate over the rest of the products
    for (const product of products) {
      if (!product.sex?.length) continue;
      const genders = new Set(product.sex);

      // Intersect the sets
      commonTypes = new Set(
        [...commonTypes].filter((type) => genders.has(type))
      );
    }

    // Convert the set back to an array
    return Array.from(commonTypes);
  }

  public productOrder(a: string, b: string): number {
    return this.productsOrder.indexOf(a.toLowerCase().split(' ')[1]) <
      this.productsOrder.indexOf(b.toLowerCase().split(' ')[1])
      ? -1
      : 1;
  }

  public productAdditionalFields(
    dataInputType: DataInputType
  ): ProductAdditionalFields[] {
    const fields: ProductAdditionalFields[] = [];

    if (dataInputType === DataInputType.CANCERSCREEN) {
      fields.push(ProductAdditionalFields.Sex);
    } else if (
      dataInputType === DataInputType.NIPT ||
      dataInputType === DataInputType.MONO
    ) {
      fields.push(
        ProductAdditionalFields.IncidentalFindings,
        ProductAdditionalFields.ProductSubTypes
      );
    } else if (
      dataInputType === DataInputType.RH ||
      dataInputType === DataInputType.CARRIER
    ) {
      fields.push(ProductAdditionalFields.ProductSubTypes);
    }

    if (
      sampleFieldConfiguration[dataInputType]?.includes(SampleFields.SampleType)
    ) {
      fields.push(ProductAdditionalFields.SampleTypes);
    }

    // Patient address isn't tied to a data input type, any product can require it
    if (dataInputType) {
      fields.push(ProductAdditionalFields.RequirePatientAddress);
    }

    return fields;
  }
}
